// Economy panel — Mail / Auction / Bank with clickable lists, no raw IDs.

import type { Net } from "../net.js";
import type { ClientStore, ClientState } from "../state.js";
import { openPanel, ensurePanelStyles, escape } from "./shared_panel.js";

type Tab = "mail" | "auction" | "bank";
let currentTab: Tab = "mail";

export function openEconomyPanel(net: Net, store: ClientStore, tab: Tab = "mail"): void {
  ensurePanelStyles();
  currentTab = tab;
  net.send({ t: "mail_inbox" });
  net.send({ t: "auction_browse" });
  net.send({ t: "bank_list" });

  openPanel({
    id: "economy-overlay",
    title: "Market & Post",
    store,
    render: (s) => `
      <div class="panel-tabs">
        ${(["mail","auction","bank"] as const).map((t) =>
          `<button class="panel-tab ${t === currentTab ? "active" : ""}" data-tab="${t}">${t.charAt(0).toUpperCase() + t.slice(1)}</button>`).join("")}
        <span style="margin-left:auto;align-self:center;color:var(--dim);font-size:11px;">
          Tier: ${escape(s.entitlements.tier)}
        </span>
      </div>
      ${currentTab === "mail" ? renderMail(s) : ""}
      ${currentTab === "auction" ? renderAuction(s) : ""}
      ${currentTab === "bank" ? renderBank(s) : ""}
    `,
    bind: (root, s, close, refresh) => {
      for (const btn of root.querySelectorAll<HTMLButtonElement>(".panel-tab")) {
        btn.addEventListener("click", () => { currentTab = btn.dataset.tab as Tab; refresh(); });
      }
      bindMail(root, s, net, refresh);
      bindAuction(root, s, net, refresh);
      bindBank(root, s, net, refresh);
      void close;
    },
  });
}

// ── MAIL ──────────────────────────────────────────────────────

function renderMail(s: ClientState): string {
  const mail = s.mail;
  const msgs = mail.messages;
  return `
    <div style="display:flex;gap:6px;margin-bottom:8px;">
      <button id="mail-inbox-btn">Refresh Inbox</button>
      <button id="mail-sent-btn">Sent</button>
      <span class="panel-hint" style="margin-left:auto;align-self:center;">${mail.count}/${mail.capacity}</span>
    </div>
    ${msgs.length === 0 ? `<div class="panel-hint">No mail.</div>` : `
      <div class="mail-list">
        ${msgs.map((m) => `
          <div class="mail-row ${m.read_at === 0 ? "unread" : ""}" data-mail-id="${m.id}">
            <div class="mail-header">
              <span class="mail-dir">${m.direction === "sent" ? "→" : "←"}</span>
              <span class="mail-sender">${escape(m.sender)}</span>
              <span class="mail-subject">${escape(m.subject)}</span>
              ${m.attached_gold > 0 ? `<span class="mail-gold">+${m.attached_gold}g</span>` : ""}
              ${m.cod_gold > 0 ? `<span class="mail-cod">COD ${m.cod_gold}g</span>` : ""}
            </div>
            <div class="mail-actions">
              <button class="mail-read-btn" data-mid="${m.id}">Read</button>
              <button class="mail-delete-btn" data-mid="${m.id}" style="color:var(--red);">Delete</button>
              ${m.cod_gold > 0 ? `<label style="font-size:11px;color:var(--dim);"><input type="checkbox" class="mail-accept-cod" data-mid="${m.id}" /> Accept COD</label>` : ""}
            </div>
          </div>
        `).join("")}
      </div>
    `}
    <hr style="border:0;border-top:1px solid var(--line);margin:12px 0;">
    <div class="panel-section-title">Send Mail</div>
    <div style="display:grid;grid-template-columns:1fr 2fr;gap:6px;margin-bottom:6px;">
      <input id="mail-to" type="text" placeholder="Recipient name" list="mail-recipients" />
      <datalist id="mail-recipients">
        ${(s.room?.others ?? []).map((o) => `<option value="${escape(o)}">`).join("")}
      </datalist>
      <input id="mail-subject" type="text" placeholder="Subject" />
    </div>
    <textarea id="mail-body" placeholder="Message body..." rows="3"></textarea>
    <div style="display:flex;gap:6px;margin-top:6px;align-items:center;">
      <input id="mail-gold" type="number" placeholder="Gold (0)" style="width:80px;" min="0" />
      <select id="mail-item" style="flex:1;">
        <option value="0">No item attachment</option>
        ${(s.inventory ?? []).map((i) => `<option value="${i.id}">${escape(i.name)} ${i.qty > 1 ? `(×${i.qty})` : ""}</option>`).join("")}
      </select>
      <button id="mail-send-btn">Send</button>
    </div>
  `;
}

function bindMail(root: HTMLElement, _s: ClientState, net: Net, refresh: () => void): void {
  root.querySelector("#mail-inbox-btn")?.addEventListener("click", () => { net.send({ t: "mail_inbox" }); setTimeout(refresh, 500); });
  root.querySelector("#mail-sent-btn")?.addEventListener("click", () => { net.send({ t: "mail_sent_box" }); setTimeout(refresh, 500); });
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".mail-read-btn")) {
    btn.addEventListener("click", () => {
      const mid = Number(btn.dataset.mid);
      const codCheckbox = root.querySelector<HTMLInputElement>(`.mail-accept-cod[data-mid="${mid}"]`);
      net.send({ t: "mail_read", mail_id: mid, accept_cod: codCheckbox?.checked ?? false });
    });
  }
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".mail-delete-btn")) {
    btn.addEventListener("click", () => {
      net.send({ t: "mail_delete", mail_id: Number(btn.dataset.mid) });
      setTimeout(() => { net.send({ t: "mail_inbox" }); setTimeout(refresh, 300); }, 200);
    });
  }
  root.querySelector("#mail-send-btn")?.addEventListener("click", () => {
    const to = (root.querySelector<HTMLInputElement>("#mail-to")!).value.trim();
    const subject = (root.querySelector<HTMLInputElement>("#mail-subject")!).value.trim();
    const body = (root.querySelector<HTMLTextAreaElement>("#mail-body")!).value.trim();
    const gold = Number(root.querySelector<HTMLInputElement>("#mail-gold")?.value ?? 0) || 0;
    const item = Number(root.querySelector<HTMLSelectElement>("#mail-item")?.value ?? 0) || 0;
    if (!to || !subject) return;
    net.send({ t: "mail_send", to, subject, body, gold, cod_gold: 0, item_instance_id: item > 0 ? item : undefined });
  });
}

// ── AUCTION ───────────────────────────────────────────────────

function renderAuction(s: ClientState): string {
  const a = s.auction;
  return `
    <div style="display:flex;gap:6px;margin-bottom:8px;">
      <input id="ah-query" type="text" placeholder="Search items..." style="flex:1;" />
      <button id="ah-browse-btn">Search</button>
      <button id="ah-mine-btn">My Listings</button>
      <span class="panel-hint" style="align-self:center;">${a.my_active}/${a.my_capacity} slots</span>
    </div>
    ${a.listings.length === 0 ? `<div class="panel-hint">No listings found.</div>` : `
      <div class="auction-list">
        ${a.listings.map((l) => `
          <div class="auction-row ${l.is_mine ? "mine" : ""}">
            <div class="auction-item">
              <span class="auction-name">${l.qty}× <strong>${escape(l.item_name)}</strong></span>
              <span class="auction-seller">${escape(l.seller)}${l.is_mine ? " (you)" : ""}</span>
            </div>
            <div class="auction-prices">
              <span>Ask: ${l.asking_gold}g</span>
              ${l.buyout_gold > 0 ? `<span>Buyout: ${l.buyout_gold}g</span>` : ""}
              ${l.current_bid > 0 ? `<span>Bid: ${l.current_bid}g</span>` : ""}
              ${l.has_reserve ? `<span class="${l.reserve_met ? "met" : "unmet"}">${l.reserve_met ? "Reserve met" : "Reserve not met"}</span>` : ""}
            </div>
            <div class="auction-actions">
              ${!l.is_mine ? `
                <button class="ah-buy-btn" data-lid="${l.id}" ${l.buyout_gold <= 0 ? "disabled" : ""}>Buyout</button>
                <input class="ah-bid-input" data-lid="${l.id}" type="number" placeholder="bid" style="width:70px;" min="${l.asking_gold}" />
                <button class="ah-bid-btn" data-lid="${l.id}">Bid</button>
              ` : `
                <button class="ah-cancel-btn" data-lid="${l.id}" style="color:var(--red);">Cancel</button>
              `}
            </div>
          </div>
        `).join("")}
      </div>
    `}
    <hr style="border:0;border-top:1px solid var(--line);margin:12px 0;">
    <div class="panel-section-title">List an item for sale</div>
    <div style="display:flex;gap:6px;align-items:center;">
      <select id="ah-item" style="flex:1;">
        <option value="0">Select item from inventory</option>
        ${(s.inventory ?? []).filter((i) => !i.equipped_slot).map((i) => `<option value="${i.id}">${escape(i.name)} ${i.qty > 1 ? `(×${i.qty})` : ""}</option>`).join("")}
      </select>
      <input id="ah-qty" type="number" placeholder="qty" style="width:60px;" min="1" value="1" />
      <input id="ah-price" type="number" placeholder="asking gold" style="width:100px;" min="1" />
      <input id="ah-buyout" type="number" placeholder="buyout (opt)" style="width:100px;" />
      <button id="ah-list-btn">List</button>
    </div>
  `;
}

function bindAuction(root: HTMLElement, _s: ClientState, net: Net, refresh: () => void): void {
  root.querySelector("#ah-browse-btn")?.addEventListener("click", () => {
    const q = root.querySelector<HTMLInputElement>("#ah-query")?.value.trim();
    net.send({ t: "auction_browse", query: q || undefined });
    setTimeout(refresh, 500);
  });
  root.querySelector("#ah-mine-btn")?.addEventListener("click", () => { net.send({ t: "auction_mine" }); setTimeout(refresh, 500); });
  root.querySelector("#ah-list-btn")?.addEventListener("click", () => {
    const inst = Number(root.querySelector<HTMLSelectElement>("#ah-item")?.value ?? 0);
    const qty = Number(root.querySelector<HTMLInputElement>("#ah-qty")?.value ?? 1) || 1;
    const price = Number(root.querySelector<HTMLInputElement>("#ah-price")?.value ?? 0);
    const buyout = Number(root.querySelector<HTMLInputElement>("#ah-buyout")?.value ?? 0);
    if (!inst || price <= 0) return;
    net.send({ t: "auction_list", instance_id: inst, qty, asking_gold: price, buyout_gold: buyout > 0 ? buyout : undefined });
  });
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".ah-buy-btn")) {
    btn.addEventListener("click", () => net.send({ t: "auction_buy", listing_id: Number(btn.dataset.lid) }));
  }
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".ah-bid-btn")) {
    btn.addEventListener("click", () => {
      const lid = Number(btn.dataset.lid);
      const input = root.querySelector<HTMLInputElement>(`.ah-bid-input[data-lid="${lid}"]`);
      const amt = Number(input?.value ?? 0);
      if (amt > 0) net.send({ t: "auction_bid", listing_id: lid, bid_gold: amt });
    });
  }
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".ah-cancel-btn")) {
    btn.addEventListener("click", () => net.send({ t: "auction_cancel", listing_id: Number(btn.dataset.lid) }));
  }
}

// ── BANK ──────────────────────────────────────────────────────

function renderBank(s: ClientState): string {
  const bank = (s as unknown as { bank?: { tabs: number; labels: Record<number, string>; items: Array<{ id: number; tab: number; name: string; qty: number }> } }).bank;
  const items = bank?.items ?? [];
  const labels = bank?.labels ?? {};
  const tabCount = bank?.tabs ?? 1;
  return `
    <div style="display:flex;gap:6px;margin-bottom:8px;">
      ${Array.from({length: tabCount}, (_, i) => `
        <button class="bank-tab-btn panel-tab" data-btab="${i + 1}">${escape(labels[i + 1] ?? `Tab ${i + 1}`)}</button>
      `).join("")}
      <button id="bank-refresh-btn" style="margin-left:auto;">Refresh</button>
    </div>
    ${items.length === 0 ? `<div class="panel-hint">Bank is empty.</div>` : `
      <div class="bank-list">
        ${items.map((item) => `
          <div class="bank-row">
            <span class="bank-item-name">${escape(item.name)} ${item.qty > 1 ? `(×${item.qty})` : ""}</span>
            <span class="bank-item-tab" style="color:var(--dim);font-size:11px;">Tab ${item.tab}</span>
            <button class="bank-withdraw-btn" data-bid="${item.id}">Withdraw</button>
          </div>
        `).join("")}
      </div>
    `}
    <hr style="border:0;border-top:1px solid var(--line);margin:12px 0;">
    <div class="panel-section-title">Deposit from inventory</div>
    <div style="display:flex;gap:6px;align-items:center;">
      <select id="bank-deposit-item" style="flex:1;">
        <option value="0">Select item</option>
        ${(s.inventory ?? []).filter((i) => !i.equipped_slot).map((i) => `<option value="${i.id}">${escape(i.name)} ${i.qty > 1 ? `(×${i.qty})` : ""}</option>`).join("")}
      </select>
      <select id="bank-deposit-tab">
        ${Array.from({length: tabCount}, (_, i) => `<option value="${i + 1}">${escape(labels[i + 1] ?? `Tab ${i + 1}`)}</option>`).join("")}
      </select>
      <button id="bank-deposit-btn">Deposit</button>
    </div>
  `;
}

function bindBank(root: HTMLElement, _s: ClientState, net: Net, refresh: () => void): void {
  root.querySelector("#bank-refresh-btn")?.addEventListener("click", () => { net.send({ t: "bank_list" }); setTimeout(refresh, 500); });
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".bank-tab-btn")) {
    btn.addEventListener("click", () => { net.send({ t: "bank_list", tab: Number(btn.dataset.btab) }); setTimeout(refresh, 500); });
  }
  for (const btn of root.querySelectorAll<HTMLButtonElement>(".bank-withdraw-btn")) {
    btn.addEventListener("click", () => { net.send({ t: "bank_withdraw", bank_id: Number(btn.dataset.bid) }); setTimeout(refresh, 500); });
  }
  root.querySelector("#bank-deposit-btn")?.addEventListener("click", () => {
    const inst = Number(root.querySelector<HTMLSelectElement>("#bank-deposit-item")?.value ?? 0);
    const tab = Number(root.querySelector<HTMLSelectElement>("#bank-deposit-tab")?.value ?? 1);
    if (!inst) return;
    net.send({ t: "bank_deposit", instance_id: inst, tab });
    setTimeout(refresh, 500);
  });
}
